if (!customElements.get("auto-modal")) {
  // Fallback used when component-popup-manager.js was not loaded: every claim
  // succeeds, so popups behave exactly as they did before queueing existed.
  const NO_MANAGER = {
    isBusy: () => false,
    claim: () => true,
    release: () => {},
    enqueue: () => false,
    enableDebug: () => {},
  };

  const popupManager = () => window.themePopupManager || NO_MANAGER;

  class AutoModal extends HTMLElement {
    static getOpenModal() {
      return document.querySelector("auto-modal[open]");
    }

    static getDismissedKey() {
      return "autoModalLastDismissedAt";
    }

    static getSessionStartKey() {
      return "autoModalSessionStartedAt";
    }

    static getConfirmedSuffix() {
      return "-confirmed";
    }

    getConfirmedKey() {
      return `${this.id}${AutoModal.getConfirmedSuffix()}`;
    }

    _log(...args) {
      if (this.debug) console.log(`[AutoModal#${this.id}]`, ...args);
    }

    isConfirmed() {
      if (!this.debug && this.hasAttribute("data-confirmed")) {
        this._log("isConfirmed → true (data-confirmed attribute set)");
        return true;
      }

      try {
        const raw = window.localStorage.getItem(this.getConfirmedKey());
        this._log(`isConfirmed localStorage[${this.getConfirmedKey()}] =`, raw);
        if (!raw) {
          this._log("isConfirmed → false (no localStorage entry)");
          return false;
        }

        try {
          const entry = JSON.parse(raw);
          if (entry && entry.value === "true" && entry.savedAt) {
            if (this.debug) {
              const age = Date.now() - entry.savedAt;
              const withinTTL = age < 15 * 1000;
              this._log(`isConfirmed → ${withinTTL} (debug TTL: ${(age / 1000).toFixed(1)}s old, TTL=15s)`);
              return withinTTL;
            }
            this._log("isConfirmed → true (JSON entry, no debug TTL)");
            return true;
          }
        } catch (e) {
          // Legacy plain value
        }

        const result = raw === "true" || raw === "1";
        this._log(`isConfirmed → ${result} (legacy value: "${raw}")`);
        return result;
      } catch (e) {
        return false;
      }
    }

    markConfirmed() {
      this.setAttribute("data-confirmed", "true");
      try {
        if (!window.localStorage.getItem(this.getConfirmedKey())) {
          window.localStorage.setItem(
            this.getConfirmedKey(),
            JSON.stringify({ value: "true", savedAt: Date.now() }),
          );
        }
      } catch (e) {
        // Ignore localStorage failures
      }
    }

    getSessionStartedAt() {
      try {
        const existing = window.sessionStorage.getItem(
          AutoModal.getSessionStartKey(),
        );
        if (existing) return parseInt(existing, 10);

        const now = Date.now();
        window.sessionStorage.setItem(
          AutoModal.getSessionStartKey(),
          String(now),
        );
        return now;
      } catch (e) {
        return Date.now();
      }
    }

    hasMetExitIntentMinTime() {
      if (this.debug) return true;
      const startedAt = this.getSessionStartedAt();
      return Date.now() - startedAt >= 60 * 1000;
    }

    getLastDismissedAt() {
      try {
        const value = window.sessionStorage.getItem(
          AutoModal.getDismissedKey(),
        );
        return value ? parseInt(value, 10) : 0;
      } catch (e) {
        return 0;
      }
    }

    markDismissed() {
      try {
        window.sessionStorage.setItem(
          AutoModal.getDismissedKey(),
          String(Date.now()),
        );
      } catch (e) {
        // Ignore sessionStorage failures
      }
    }

    isInDismissCooldown() {
      const lastDismissedAt = this.getLastDismissedAt();
      if (!lastDismissedAt) return false;
      return Date.now() - lastDismissedAt < 120 * 1000;
    }

    canOpen({ ignoreDismissCooldown = false, ignoreBusy = false } = {}) {
      if (this.isConfirmed()) {
        this._log("canOpen → false (confirmed)");
        return false;
      }

      // The one-popup-at-a-time rule holds in debug mode too — stacked popups
      // in the customizer are the main thing it is there to prevent.
      if (!ignoreBusy && popupManager().isBusy(this)) {
        this._log("canOpen → false (another popup holds the open slot)");
        return false;
      }

      if (this.debug) {
        this._log("canOpen → true (debug mode, skipping dismiss cooldown)");
        return true;
      }

      if (!ignoreDismissCooldown && this.isInDismissCooldown()) {
        const msLeft = 120 * 1000 - (Date.now() - this.getLastDismissedAt());
        this._log(`canOpen → false (dismiss cooldown: ${(msLeft / 1000).toFixed(0)}s remaining)`);
        return false;
      }

      this._log("canOpen → true");
      return true;
    }

    constructor() {
      super();
      this.autoOpen = !this.hasAttribute("data-closed");
      this.delay = parseInt(this.getAttribute("data-delay"));
      this.debug = this.getAttribute("data-debug");
      this.show = parseInt(this.getAttribute("data-show"));
      this.id = this.getAttribute("id");
      this.trigger = this.getAttribute("data-trigger") || "automatic";
      this.isOpen = false;
      this.exitIntentTriggered = false;
      this._hideTimer = null;

      if (this.debug) popupManager().enableDebug();

      const self = this;
      document.addEventListener(
        "DOMContentLoaded",
        self.setupButtons.bind(self),
      );

      this.style.setProperty("display", "none");

      this.querySelector('[id^="ModalClose-"]')?.addEventListener(
        "click",
        this.hide.bind(this),
      );
      this.querySelectorAll("[data-close-modal]").forEach((closer) =>
        closer.addEventListener("click", this.hide.bind(this, false)),
      );

      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE") this.hide();
      });
      this.addEventListener("click", (event) => {
        if (event.target.nodeName === "AUTO-MODAL") this.hide();
      });

      if (this.autoOpen) {
        if (this.trigger === "exit_intent") {
          this.setupExitIntent();
        } else {
          window.setTimeout(self.tryToOpen.bind(self), this.delay * 1000);
        }
      }
    }

    /**
     * Exit intent fired: attempt to open, and keep the trigger armed when the
     * attempt was merely blocked (a queued attempt stays queued instead).
     */
    handleExitIntent() {
      this.exitIntentTriggered = true;
      if (this.tryToOpen() === "blocked") this.exitIntentTriggered = false;
    }

    setupExitIntent() {
      const self = this;

      // Establish session start for cross-page gating
      self.getSessionStartedAt();

      // Only trigger on desktop (mouse-based devices)
      if (window.matchMedia("(pointer: fine)").matches) {
        document.addEventListener("mouseout", function handleMouseOut(e) {
          // Check if mouse is leaving viewport at the top
          if (
            e.clientY <= 0 &&
            self.hasMetExitIntentMinTime() &&
            !self.exitIntentTriggered &&
            !self.isOpen
          ) {
            self.handleExitIntent();
          }
        });
      } else {
        // Fallback for touch devices: use scroll up detection or delayed trigger
        let lastScrollY = window.scrollY;
        let scrollUpCount = 0;

        window.addEventListener("scroll", function handleScroll() {
          if (window.scrollY < lastScrollY) {
            scrollUpCount++;
            // Trigger after scrolling up significantly from lower in the page
            if (
              self.hasMetExitIntentMinTime() &&
              scrollUpCount > 3 &&
              lastScrollY > window.innerHeight &&
              !self.exitIntentTriggered &&
              !self.isOpen
            ) {
              self.handleExitIntent();
            }
          } else {
            scrollUpCount = 0;
          }
          lastScrollY = window.scrollY;
        });
      }
    }

    setupButtons() {
      const now = new Date();
      const self = this;

      const openers = document.querySelectorAll(
        '[id^="ModalOpen-' + this.id + '"],' + '[data-open="' + this.id + '"]',
      );
      if (openers) {
        const self = this;
        openers.forEach((el) => {
          el.addEventListener("click", self.open.bind(this));
        });
      }
      document
        .querySelectorAll(`[data-dismiss-open="${this.id}"]`)
        .forEach((el) =>
          el.addEventListener("click", self.dismissOpenButton.bind(self)),
        );

      let shouldOpen = true;
      if (this.isConfirmed()) {
        this._log("setupButtons → button hidden (confirmed)");
        shouldOpen = false;
      }

      const dismissedVal = localStorage.getItem("popupButtonDismissed-" + this.id);
      if (dismissedVal) {
        if (parseInt(dismissedVal) <= now.getTime()) {
          this._log("setupButtons → button hidden (popupButtonDismissed expired or set)");
          shouldOpen = false;
        } else {
          this._log(`setupButtons → popupButtonDismissed found but not yet elapsed:`, dismissedVal);
        }
      }

      this._log(`setupButtons → shouldOpen: ${shouldOpen}`);
      if (shouldOpen) {
        document
          .querySelectorAll('[data-open="' + this.id + '"]')
          .forEach((el) => {
            el.parentElement.classList.add("active");
            el.parentElement.setAttribute("tabindex", "0");
          });
      }
    }

    dismissOpenButton() {
      const now = new Date();

      localStorage.setItem(
        "popupButtonDismissed-" + this.id,
        now.getTime() + 6000,
      );

      document
        .querySelectorAll('[data-open="' + this.id + '"][data-dismissable]')
        .forEach((el) => {
          el.parentElement.classList.add("dismissed");
          el.parentElement.setAttribute("tabindex", "-1");
          el.setAttribute("disabled", "true");
        });
    }

    /**
     * Open right now. Used for explicit opens (trigger button, cart upsell), so
     * it takes the open slot outright and closes any popup already using it.
     */
    open() {
      this._log("open() called");
      if (!this.canOpen({ ignoreDismissCooldown: true, ignoreBusy: true })) {
        this._log("open() → blocked by canOpen");
        return;
      }
      if (this.isOpen) {
        this._log("open() → already open, skipping");
        return;
      }
      if (!popupManager().claim(this, { force: true })) {
        this._log("open() → blocked (could not claim the open slot)");
        return;
      }
      this._log("open() → opening modal");
      if (this._hideTimer) {
        window.clearTimeout(this._hideTimer);
        this._hideTimer = null;
      }
      this.style.removeProperty("display");
      this.isOpen = true;
      document.body.classList.add("overflow-hidden");
      this.setAttribute("open", "");
      trapFocus(this, this.querySelector(".modal-content"));
      // window.pauseAllMedia();
      this.setStorage();
    }

    hide() {
      const wasOpen = this.isOpen;
      this.isOpen = false;
      document.body.classList.remove("overflow-hidden");
      this.removeAttribute("open");
      // window.pauseAllMedia();

      if (wasOpen && typeof removeTrapFocus === "function") removeTrapFocus();

      // A popup displaced by an explicit open was not dismissed by the
      // visitor, so it must not start the cross-page dismiss cooldown.
      if (wasOpen && !this.__themePopupDisplacing) {
        this.markDismissed();
      }

      if (this._hideTimer) window.clearTimeout(this._hideTimer);
      this._hideTimer = window.setTimeout(() => {
        this._hideTimer = null;
        this.style.setProperty("display", "none");
      }, 1000);

      // Hands the slot back and starts the countdown to the next queued popup.
      popupManager().release(this);
    }

    /**
     * Automatic open attempt — delay timer, exit intent, or a turn from the
     * queue. Never stacks: when another popup holds the open slot this popup
     * queues itself instead, and the manager retries it after the gap.
     *
     * Returns "opened", "queued" or "blocked".
     */
    tryToOpen({ fromQueue = false } = {}) {
      this._log(`tryToOpen() called${fromQueue ? " (from queue)" : ""}`);

      // A queued popup has already waited out the manager's gap since the last
      // popup closed, so the cross-page dismiss cooldown would only cancel a
      // turn it just earned.
      if (!this.shouldOpenNow({ ignoreDismissCooldown: fromQueue })) {
        this._log("tryToOpen → blocked");
        return "blocked";
      }

      const manager = popupManager();
      if (manager.isBusy(this)) {
        manager.enqueue(this);
        this._log("tryToOpen → queued (another popup is open)");
        return "queued";
      }

      this.open();
      return this.isOpen ? "opened" : "blocked";
    }

    /**
     * Every condition on showing this popup right now apart from overlap:
     * confirmed state, dismiss cooldown, splash pages and show frequency.
     */
    shouldOpenNow({ ignoreDismissCooldown = false } = {}) {
      if (this.isOpen) {
        this._log("shouldOpenNow → false (already open)");
        return false;
      }

      if (window.IS_FS_SPLASH === true) {
        this._log("shouldOpenNow → false (splash page)");
        return false;
      }

      if (!this.canOpen({ ignoreDismissCooldown, ignoreBusy: true })) {
        return false;
      }

      const params = new URLSearchParams(window.location.search);
      if (params.has("openPopups")) {
        this._log("shouldOpenNow → true (openPopups param)");
        return true;
      }

      if (this.debug) {
        this._log("shouldOpenNow → true (debug mode)");
        return true;
      }

      if (this.show > 0) {
        const stored = localStorage.getItem(this.id);
        this._log(`shouldOpenNow show>0: localStorage[${this.id}] =`, stored);
        if (!stored) {
          this._log("shouldOpenNow → true (no show interval stored)");
          return true;
        }
        if (parseInt(stored) <= Date.now()) {
          this._log("shouldOpenNow → true (show interval elapsed)");
          return true;
        }
        this._log(`shouldOpenNow → false (show interval not elapsed, ${((parseInt(stored) - Date.now()) / 1000 / 60).toFixed(1)} min remaining)`);
        return false;
      }

      // Popups without a show frequency (e.g. the cart upsell) are eligible on
      // every attempt; the dismiss cooldown above is what paces them.
      this._log(
        `shouldOpenNow → true (no show frequency, sessionStorage[${this.id}] =`,
        window.sessionStorage.getItem(this.id),
        ")",
      );
      return true;
    }

    setStorage() {
      const now = new Date();
      localStorage.setItem(this.id, now.getTime() + this.show * 60 * 60 * 1000);
      try {
        if (!(this.show > 0)) {
          window.sessionStorage.setItem(this.id, "shown");
        }
      } catch (e) {
        // Ignore sessionStorage failures
      }
    }
  }

  customElements.define("auto-modal", AutoModal);
}
